import { useCallback, useEffect, useState } from "react"
import { ShieldCheck } from "lucide-react"
import RequireRole from "../../common/validation/require-role"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants/navbar"
import PageHeader from "../../common/ui/page-header"
import SidePanel from "../../common/ui/side-panel"
import { ListSkeleton } from "../../common/ui/skeletons"
import EmptyState from "../../common/ui/empty-state"
import Badge from "../../common/ui/badge"
import { DefaultBtn } from "../../common/ui/buttons"
import ExpandableObjectViewer from "../../common/ui/expandable-object-viewer"
import { DateRangePreset } from "../../common/ui/activity/date-filter"
import {
  dateRangeToTimestamps,
  formatDateTimeFromTimestamp
} from "../../common/utils/datetime"
import bbApi from "../../api"
import { AuditLog, AuditTimelineBucket } from "../../api/audit-logs"
import FilterBar from "./filter-bar"
import TimelineChart from "./timeline-chart"

type TimelineInterval = "hour" | "day" | "week"

const DANGER_EVENTS = new Set(["auth_fail", "account_lockout", "billing_error"])
const SUCCESS_EVENTS = new Set(["auth_success", "role_grant", "api_key_create"])
const WARNING_EVENTS = new Set([
  "masquerade_start",
  "masquerade_end",
  "config_change",
  "data_delete",
  "data_export"
])

const EventTypeBadge = ({ eventType }: { eventType: string }) => {
  const label = eventType.replace(/_/g, " ")
  if (DANGER_EVENTS.has(eventType)) return <Badge.Danger>{label}</Badge.Danger>
  if (SUCCESS_EVENTS.has(eventType))
    return <Badge.Success>{label}</Badge.Success>
  if (WARNING_EVENTS.has(eventType))
    return <Badge.Warning>{label}</Badge.Warning>
  return <Badge.Info>{label}</Badge.Info>
}

const SeverityBadge = ({ severity }: { severity: string }) => {
  switch (severity) {
    case "3":
      return <Badge.Danger>Critical</Badge.Danger>
    case "2":
      return <Badge.Warning>Warning</Badge.Warning>
    default:
      return <Badge.Info>Info</Badge.Info>
  }
}

const severityRowClass = (severity: string): string => {
  switch (severity) {
    case "3":
      return "border-l-2 border-l-red-400"
    case "2":
      return "border-l-2 border-l-yellow-400"
    default:
      return "border-l-2 border-l-transparent"
  }
}

const DetailRow = ({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className="py-3 border-b border-neutral-100 last:border-0">
    <dt className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-1">
      {label}
    </dt>
    <dd className="text-sm text-neutral-800">{children}</dd>
  </div>
)

const LogDetailPanel = ({
  log,
  open,
  onClose
}: {
  log: AuditLog | null
  open: boolean
  onClose: () => void
}) => {
  if (!log) return null

  return (
    <SidePanel
      title="Audit log detail"
      subTitle={`ID: ${log.id}`}
      open={open}
      onClose={onClose}
      size="md"
    >
      <div className="p-6 space-y-0">
        <DetailRow label="Timestamp">
          {formatDateTimeFromTimestamp(
            log.created,
            "LLL dd yyyy 'at' h:mm:ss a"
          )}
        </DetailRow>

        <DetailRow label="Event type">
          <EventTypeBadge eventType={log.event_type} />
        </DetailRow>

        <DetailRow label="Severity">
          <SeverityBadge severity={log.severity} />
        </DetailRow>

        <DetailRow label="User ID">{log.user_id || "—"}</DetailRow>

        <DetailRow label="Target ID">{log.target_id || "—"}</DetailRow>

        <DetailRow label="IP address">
          <span className="font-mono">{log.ip_address || "—"}</span>
        </DetailRow>

        <DetailRow label="Log hash">
          <span className="font-mono text-xs break-all">{log.log_hash}</span>
        </DetailRow>

        {log.details && (
          <DetailRow label="Details">
            <div className="mt-1 p-3 bg-neutral-50 rounded-lg border border-neutral-200 text-sm">
              <ExpandableObjectViewer data={log.details} limit={10} />
            </div>
          </DetailRow>
        )}
      </div>
    </SidePanel>
  )
}

const AuditLogsPage = () => {
  // Filter state
  const [dateRange, setDateRange] = useState<DateRangePreset>("last_30")
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null)
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([])
  const [selectedSeverities, setSelectedSeverities] = useState<string[]>([])
  const [search, setSearch] = useState("")
  const [userId, setUserId] = useState("")
  const [targetId, setTargetId] = useState("")
  const [ipAddress, setIpAddress] = useState("")
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Detail panel state
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)

  // Log state
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [isLoadingLogs, setIsLoadingLogs] = useState(true)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Timeline state
  const [buckets, setBuckets] = useState<AuditTimelineBucket[]>([])
  const [isLoadingTimeline, setIsLoadingTimeline] = useState(true)
  const [timelineInterval, setTimelineInterval] =
    useState<TimelineInterval>("day")

  const buildParams = useCallback(() => {
    const { min, max } = dateRangeToTimestamps(
      dateRange,
      customStartDate,
      customEndDate,
      { unit: "seconds" }
    )
    const params: Record<string, string | number | boolean | undefined> = {
      from: min,
      to: max,
      event_type:
        selectedEventTypes.length > 0
          ? selectedEventTypes.join(",")
          : undefined,
      severity:
        selectedSeverities.length > 0
          ? selectedSeverities.join(",")
          : undefined,
      search: search || undefined,
      user_id: userId || undefined,
      target_id: targetId || undefined,
      ip_address: ipAddress || undefined
    }
    return params
  }, [
    dateRange,
    customStartDate,
    customEndDate,
    selectedEventTypes,
    selectedSeverities,
    search,
    userId,
    targetId,
    ipAddress
  ])

  // Fetch logs
  useEffect(() => {
    let cancelled = false
    const fetchLogs = async () => {
      setIsLoadingLogs(true)
      try {
        const data = await bbApi.auditLogs.getLogs(buildParams())
        if (cancelled) return
        setLogs(data.logs)
        setNextCursor(data.next_cursor)
        setHasMore(data.has_more)
        setTotal(data.total)
      } catch {
        if (!cancelled) {
          setLogs([])
          setTotal(0)
          setHasMore(false)
        }
      } finally {
        if (!cancelled) setIsLoadingLogs(false)
      }
    }
    fetchLogs()
    return () => {
      cancelled = true
    }
  }, [buildParams])

  // Fetch timeline
  useEffect(() => {
    let cancelled = false
    const fetchTimeline = async () => {
      setIsLoadingTimeline(true)
      try {
        const data = await bbApi.auditLogs.getTimeline({
          ...buildParams(),
          interval: timelineInterval
        })
        if (cancelled) return
        setBuckets(data.buckets)
      } catch {
        if (!cancelled) setBuckets([])
      } finally {
        if (!cancelled) setIsLoadingTimeline(false)
      }
    }
    fetchTimeline()
    return () => {
      cancelled = true
    }
  }, [buildParams, timelineInterval])

  const handleLoadMore = async () => {
    if (!nextCursor || isLoadingMore) return
    setIsLoadingMore(true)
    try {
      const data = await bbApi.auditLogs.getLogs({
        ...buildParams(),
        cursor: nextCursor
      })
      setLogs(prev => [...prev, ...data.logs])
      setNextCursor(data.next_cursor)
      setHasMore(data.has_more)
    } catch {
      // keep existing logs on error
    } finally {
      setIsLoadingMore(false)
    }
  }

  const handleDateRangeChange = (
    range: DateRangePreset,
    startDate?: Date,
    endDate?: Date
  ) => {
    setDateRange(range)
    if (range === "custom" && startDate && endDate) {
      setCustomStartDate(startDate)
      setCustomEndDate(endDate)
    }
  }

  const TH_CLASS =
    "px-3 py-2 text-left uppercase text-neutral-400 text-xs font-medium"
  const TD_CLASS = "px-3 py-2 text-sm"

  const renderDetailsSummary = (details: Record<string, unknown> | null) => {
    if (!details) return "—"
    const entries = Object.entries(details)
    if (entries.length === 0) return "—"
    const preview = entries
      .slice(0, 2)
      .map(([k, v]) => `${k}: ${String(v)}`)
      .join(", ")
    const suffix = entries.length > 2 ? ` +${entries.length - 2} more` : ""
    return (
      <span className="text-neutral-600">
        <span className="truncate">{preview}</span>
        {suffix && <span className="text-neutral-400 ml-1">{suffix}</span>}
      </span>
    )
  }

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["audit"]}>
      <div className="p-6 space-y-6">
        <PageHeader title="Audit logs" />

        <TimelineChart
          buckets={buckets}
          isLoading={isLoadingTimeline}
          interval={timelineInterval}
          onIntervalChange={setTimelineInterval}
        />

        <FilterBar
          dateRange={dateRange}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          selectedEventTypes={selectedEventTypes}
          selectedSeverities={selectedSeverities}
          search={search}
          userId={userId}
          targetId={targetId}
          ipAddress={ipAddress}
          showAdvanced={showAdvanced}
          onDateRangeChange={handleDateRangeChange}
          onEventTypesChange={setSelectedEventTypes}
          onSeveritiesChange={setSelectedSeverities}
          onSearchChange={setSearch}
          onUserIdChange={setUserId}
          onTargetIdChange={setTargetId}
          onIpAddressChange={setIpAddress}
          onToggleAdvanced={() => setShowAdvanced(prev => !prev)}
        />

        {/* Log table */}
        {isLoadingLogs ? (
          <ListSkeleton noOfRows={8} noOfColumns={4} />
        ) : logs.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            heading="No audit logs found"
            subHeading="Try adjusting your filters"
          />
        ) : (
          <div className="space-y-3">
            <div className="overflow-x-auto bg-white rounded-lg border border-neutral-200">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className={TH_CLASS}>Timestamp</th>
                    <th className={TH_CLASS}>Event</th>
                    <th className={TH_CLASS}>Severity</th>
                    <th className={TH_CLASS}>User</th>
                    <th className={TH_CLASS}>IP</th>
                    <th className={TH_CLASS}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className={`border-b border-zinc-200 hover:bg-neutral-50 cursor-pointer transition-colors ${severityRowClass(
                        log.severity
                      )}`}
                    >
                      <td
                        className={`${TD_CLASS} text-neutral-600 whitespace-nowrap`}
                      >
                        {formatDateTimeFromTimestamp(log.created)}
                      </td>
                      <td className={`${TD_CLASS} whitespace-nowrap`}>
                        <EventTypeBadge eventType={log.event_type} />
                      </td>
                      <td className={`${TD_CLASS} whitespace-nowrap`}>
                        <SeverityBadge severity={log.severity} />
                      </td>
                      <td
                        className={`${TD_CLASS} text-neutral-700 whitespace-nowrap`}
                      >
                        {log.user_id || "—"}
                      </td>
                      <td
                        className={`${TD_CLASS} text-neutral-500 whitespace-nowrap font-mono text-xs`}
                      >
                        {log.ip_address || "—"}
                      </td>
                      <td className={`${TD_CLASS} max-w-xs truncate`}>
                        {renderDetailsSummary(log.details)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral-500">
                Showing {logs.length} of {total} logs
              </span>
              {hasMore && (
                <DefaultBtn onClick={handleLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? "Loading..." : "Load More"}
                </DefaultBtn>
              )}
            </div>
          </div>
        )}

        <LogDetailPanel
          log={selectedLog}
          open={!!selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      </div>
    </RequireRole>
  )
}

export default AuditLogsPage
